import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Briefcase, Grid3X3, BarChart3, Settings,
  DollarSign, TrendingUp, UserPlus, Clock, CheckCircle,
  Trash2, Shield, Search, Filter, Download, RefreshCw, Star,
  MapPin, Edit, Eye, Bell, Menu, X,
  Award, Zap, Calendar, ArrowUpRight,
  Send, Loader2, BadgeCheck, LogOut, ChevronDown, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || "localpros2024";

// Admin API axios config with authentication header
const adminApi = axios.create({
  baseURL: API,
  headers: {
    'X-Admin-Key': ADMIN_KEY
  }
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [adminKey, setAdminKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pricing, setPricing] = useState({});
  const [platformFee, setPlatformFee] = useState(10);
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState({});
  const [featureToggles, setFeatureToggles] = useState({});
  const [emailCampaigns, setEmailCampaigns] = useState({ stats_by_type: {}, recent_emails: [], total_workers: 0, complete_profiles: 0, incomplete_profiles: 0 });
  const [campaignLoading, setCampaignLoading] = useState(false);
  
  // Filter states
  const [contractorFilter, setContractorFilter] = useState({ status: "all", city: "", category: "" });
  const [jobFilter, setJobFilter] = useState({ status: "all", category: "" });
  
  // Modal states
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notification, setNotification] = useState({ title: "", message: "", targetCity: "" });
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Delete confirmation states
  const [deleteContractorDialog, setDeleteContractorDialog] = useState({ open: false, id: null, name: "" });
  const [deleteJobDialog, setDeleteJobDialog] = useState({ open: false, id: null, title: "" });
  const [deleting, setDeleting] = useState(false);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, workersRes, jobsRes, leadsRes, pricingRes, bookingsRes, featuresRes] = await Promise.all([
        adminApi.get(`/admin/stats`),
        axios.get(`${API}/workers`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/service-requests`),
        adminApi.get(`/admin/pricing`),
        adminApi.get(`/admin/bookings`),
        adminApi.get(`/admin/features`)
      ]);
      
      setStats(statsRes.data);
      setContractors(workersRes.data);
      setJobs(jobsRes.data);
      setLeads(leadsRes.data);
      setPricing(pricingRes.data.plans || {});
      setPlatformFee(pricingRes.data.platform_fee_percent || 10);
      setBookings(bookingsRes.data.bookings || []);
      setBookingStats(bookingsRes.data.stats || {});
      setFeatureToggles(featuresRes.data || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminKey === ADMIN_KEY) {
      setIsAuthenticated(true);
      // Use sessionStorage instead of localStorage for better security (clears on tab close)
      sessionStorage.setItem("adminAuth", "true");
      sessionStorage.setItem("adminAuthTime", Date.now().toString());
      fetchAllData();
    } else {
      toast.error("Invalid admin key");
    }
  };

  useEffect(() => {
    // Check session storage with timeout (8 hours)
    const savedAuth = sessionStorage.getItem("adminAuth");
    const authTime = sessionStorage.getItem("adminAuthTime");
    const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
    
    if (savedAuth === "true" && authTime) {
      const elapsed = Date.now() - parseInt(authTime, 10);
      if (elapsed < SESSION_TIMEOUT) {
        setIsAuthenticated(true);
        fetchAllData();
      } else {
        // Session expired
        sessionStorage.removeItem("adminAuth");
        sessionStorage.removeItem("adminAuthTime");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    sessionStorage.removeItem("adminAuthTime");
    setIsAuthenticated(false);
  };

  // Email Campaign Functions
  const fetchEmailCampaigns = async () => {
    setCampaignLoading(true);
    try {
      const response = await adminApi.get(`/admin/email-campaigns`);
      setEmailCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to load email campaign data");
    } finally {
      setCampaignLoading(false);
    }
  };

  const triggerCampaignProcessing = async () => {
    try {
      await adminApi.post(`/admin/email-campaigns/trigger`);
      toast.success("Campaign processing triggered!");
      setTimeout(fetchEmailCampaigns, 2000);
    } catch (error) {
      toast.error("Failed to trigger campaigns");
    }
  };

  const sendTestEmail = async (workerId, campaignType) => {
    try {
      const response = await adminApi.post(`/admin/email-campaigns/send-test?worker_id=${workerId}&campaign_type=${campaignType}`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      fetchEmailCampaigns();
    } catch (error) {
      toast.error("Failed to send test email");
    }
  };

  // Actions
  
  // Load email campaigns when tab is selected
  useEffect(() => {
    if (activeTab === "email-campaigns" && isAuthenticated) {
      fetchEmailCampaigns();
    }
  }, [activeTab, isAuthenticated]);
  
  const approveContractor = async (contractorId) => {
    try {
      await axios.post(`${API}/verify/${contractorId}?verification_type=verified`);
      toast.success("Contractor approved!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to approve contractor");
    }
  };

  const deleteContractor = async (contractorId) => {
    setDeleting(true);
    try {
      console.log(`[Admin] Deleting contractor: ${contractorId}`);
      const response = await adminApi.delete(`/admin/workers/${contractorId}`);
      console.log(`[Admin] Delete response:`, response.data);
      toast.success("Contractor deleted successfully");
      setDeleteContractorDialog({ open: false, id: null, name: "" });
      await fetchAllData();
    } catch (error) {
      console.error(`[Admin] Delete contractor error:`, error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to delete contractor");
    } finally {
      setDeleting(false);
    }
  };

  const toggleFeatured = async (contractorId, featured) => {
    try {
      await adminApi.patch(`/admin/workers/${contractorId}/featured`, { is_featured: !featured });
      toast.success(featured ? "Removed from featured" : "Added to featured!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update contractor");
    }
  };

  const deleteJob = async (jobId) => {
    setDeleting(true);
    try {
      console.log(`[Admin] Deleting job: ${jobId}`);
      const response = await adminApi.delete(`/admin/jobs/${jobId}`);
      console.log(`[Admin] Delete response:`, response.data);
      toast.success("Job deleted successfully");
      setDeleteJobDialog({ open: false, id: null, title: "" });
      await fetchAllData();
    } catch (error) {
      console.error(`[Admin] Delete job error:`, error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to delete job");
    } finally {
      setDeleting(false);
    }
  };

  // Open delete confirmation dialogs
  const confirmDeleteContractor = (contractor) => {
    setDeleteContractorDialog({ 
      open: true, 
      id: contractor.id, 
      name: contractor.business_name || contractor.name 
    });
  };

  const confirmDeleteJob = (job) => {
    setDeleteJobDialog({ 
      open: true, 
      id: job.id, 
      title: job.title 
    });
  };

  const sendGeoNotification = async () => {
    try {
      await adminApi.post(`/admin/notifications`, notification);
      toast.success("Notification sent!");
      setShowNotificationModal(false);
      setNotification({ title: "", message: "", targetCity: "" });
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  // Pricing controls
  const updatePricing = async (planId, updates) => {
    try {
      await adminApi.patch(`/admin/pricing/${planId}`, { plan_id: planId, ...updates });
      toast.success("Pricing updated!");
      setShowPricingModal(false);
      setEditingPlan(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update pricing");
    }
  };

  const updatePlatformFee = async (newFee) => {
    try {
      await adminApi.patch(`/admin/platform-fee`, { fee_percent: parseFloat(newFee) });
      toast.success("Platform fee updated!");
      setPlatformFee(newFee);
    } catch (error) {
      toast.error("Failed to update platform fee");
    }
  };

  // Booking controls
  const releasePayment = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/release-payment`);
      toast.success("Payment released!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to release payment");
    }
  };

  const updateFeatureToggle = async (feature, enabled) => {
    try {
      const updatedToggles = { ...featureToggles, [feature]: enabled };
      await adminApi.patch(`/admin/features`, updatedToggles);
      setFeatureToggles(updatedToggles);
      toast.success(`Feature ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error("Failed to update feature");
    }
  };

  const exportData = (type) => {
    const data = type === "contractors" ? contractors : type === "jobs" ? jobs : leads;
    if (!data.length) return toast.error("No data to export");
    const csv = [
      Object.keys(data[0] || {}).join(","),
      ...data.map(item => Object.values(item).map(v => `"${v}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Data exported!");
  };

  const filteredContractors = contractors.filter(c => {
    if (contractorFilter.status === "pending" && c.is_verified) return false;
    if (contractorFilter.status === "verified" && !c.is_verified) return false;
    if (contractorFilter.city && !c.location?.toLowerCase().includes(contractorFilter.city.toLowerCase())) return false;
    if (contractorFilter.category && !c.services?.includes(contractorFilter.category)) return false;
    return true;
  });

  const filteredJobs = jobs.filter(j => {
    if (jobFilter.status !== "all" && j.status !== jobFilter.status) return false;
    if (jobFilter.category && j.service_category !== jobFilter.category) return false;
    return true;
  });

  // Navigation items
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "users", label: "Users", icon: Users },
    { id: "contractors", label: "Contractors", icon: Briefcase },
    { id: "jobs", label: "Job Leads", icon: Grid3X3 },
    { id: "bookings", label: "Bookings", icon: Calendar },
    { id: "email-campaigns", label: "Email Campaigns", icon: Send },
    { id: "pricing", label: "Pricing", icon: DollarSign },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="admin-login">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Admin Portal</h1>
            <p className="text-gray-500 mt-1 text-sm">LocalPros Management</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-5">
              <Label className="text-sm text-gray-600">Admin Key</Label>
              <Input
                type="password"
                placeholder="Enter admin key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                className="mt-2 h-12 rounded-xl"
                data-testid="admin-key-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-xl text-base"
              data-testid="admin-login-btn"
            >
              Access Dashboard
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top Navigation */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-gray-900 hidden sm:block">LocalPros Admin</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === item.id
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAllData}
                className="hidden sm:flex"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="max-w-7xl mx-auto px-4 py-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === item.id
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 capitalize">{activeTab}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {activeTab === "dashboard" && "Platform overview"}
              {activeTab === "users" && "Manage platform users"}
              {activeTab === "contractors" && "Manage contractors"}
              {activeTab === "jobs" && "Track job leads"}
              {activeTab === "bookings" && "Manage bookings & payments"}
              {activeTab === "email-campaigns" && "Automated email campaigns"}
              {activeTab === "pricing" && "Configure pricing & fees"}
              {activeTab === "analytics" && "Analytics & reports"}
              {activeTab === "settings" && "Platform settings"}
            </p>
          </div>
          <Button 
            onClick={() => setShowNotificationModal(true)} 
            className="bg-blue-600 hover:bg-blue-700 rounded-xl h-10"
          >
            <Bell className="w-4 h-4 mr-2" />
            Send Alert
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-green-500 ml-auto" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{contractors.length}</div>
                <div className="text-xs text-gray-500">Contractors</div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats?.overview?.new_workers_today || 0}</div>
                <div className="text-xs text-gray-500">New Today</div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">{jobs.length}</div>
                <div className="text-xs text-gray-500">Jobs Posted</div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {contractors.filter(c => !c.is_verified).length}
                </div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>

              <div className="col-span-2 lg:col-span-1 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-5 shadow-sm text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-bold">${stats?.overview?.total_revenue?.toFixed(0) || 0}</div>
                <div className="text-xs text-white/80">Revenue</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setActiveTab("contractors")} className="rounded-lg text-sm h-9">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  Approve ({contractors.filter(c => !c.is_verified).length})
                </Button>
                <Button variant="outline" onClick={() => setShowNotificationModal(true)} className="rounded-lg text-sm h-9">
                  <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                  Geo Alert
                </Button>
                <Button variant="outline" onClick={() => exportData("contractors")} className="rounded-lg text-sm h-9">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Contractors</h3>
                <div className="space-y-3">
                  {contractors.slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm shrink-0">
                          {c.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{c.name}</p>
                          <p className="text-xs text-gray-500 truncate">{c.location}</p>
                        </div>
                      </div>
                      {c.is_verified ? (
                        <BadgeCheck className="w-5 h-5 text-blue-500 shrink-0" />
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full shrink-0">Pending</span>
                      )}
                    </div>
                  ))}
                  {contractors.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No contractors yet</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Jobs</h3>
                <div className="space-y-3">
                  {jobs.slice(0, 5).map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{job.title}</p>
                        <p className="text-xs text-gray-500 truncate">{job.location}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                        job.status === "open" ? "bg-green-100 text-green-700" :
                        job.status === "assigned" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  ))}
                  {jobs.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No jobs yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Platform Users</h3>
              <p className="text-sm text-gray-500 mt-1">Homeowners and service requesters</p>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {leads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{lead.customer_name}</p>
                      <p className="text-sm text-gray-500">{lead.customer_email}</p>
                      <p className="text-xs text-gray-400 mt-1">{lead.service_category} • {lead.location}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500">{new Date(lead.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                {leads.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No users yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contractors Tab */}
        {activeTab === "contractors" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex flex-wrap items-center gap-3">
                <Filter className="w-4 h-4 text-gray-400" />
                <Select value={contractorFilter.status} onValueChange={(v) => setContractorFilter({...contractorFilter, status: v})}>
                  <SelectTrigger className="w-32 h-9 rounded-lg text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="City..."
                  value={contractorFilter.city}
                  onChange={(e) => setContractorFilter({...contractorFilter, city: e.target.value})}
                  className="w-32 h-9 rounded-lg text-sm"
                />
                <Button variant="outline" onClick={() => exportData("contractors")} className="ml-auto h-9 rounded-lg text-sm">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              </div>
            </div>

            {/* Contractors List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Contractor</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Services</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredContractors.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                              {c.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{c.business_name || c.name}</p>
                              <p className="text-xs text-gray-500">{c.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.location}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {c.services?.slice(0, 2).map((s) => (
                              <span key={s} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded capitalize">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {c.is_verified ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              <CheckCircle className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Pending</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            {!c.is_verified && (
                              <Button size="sm" variant="ghost" onClick={() => approveContractor(c.id)} className="h-8 w-8 p-0 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => toggleFeatured(c.id, c.is_featured)} className={`h-8 w-8 p-0 ${c.is_featured ? "text-yellow-500" : "text-gray-400"}`}>
                              <Star className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => window.open(`/profile/${c.id}`, '_blank')} className="h-8 w-8 p-0 text-blue-600">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => confirmDeleteContractor(c)} className="h-8 w-8 p-0 text-red-600" data-testid={`delete-contractor-${c.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredContractors.length === 0 && (
                <div className="py-12 text-center text-gray-500">No contractors found</div>
              )}
            </div>
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === "jobs" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex flex-wrap items-center gap-3">
                <Select value={jobFilter.status} onValueChange={(v) => setJobFilter({...jobFilter, status: v})}>
                  <SelectTrigger className="w-32 h-9 rounded-lg text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => exportData("jobs")} className="ml-auto h-9 rounded-lg text-sm">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              </div>
            </div>

            {/* Jobs List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredJobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-900 text-sm">{job.title}</p>
                          <p className="text-xs text-gray-500">{job.customer_name}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{job.location}</td>
                        <td className="py-3 px-4">
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded capitalize">{job.service_category}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            job.status === "open" ? "bg-green-100 text-green-700" :
                            job.status === "assigned" ? "bg-blue-100 text-blue-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end">
                            <Button size="sm" variant="ghost" onClick={() => confirmDeleteJob(job)} className="h-8 w-8 p-0 text-red-600" data-testid={`delete-job-${job.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredJobs.length === 0 && (
                <div className="py-12 text-center text-gray-500">No jobs found</div>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === "categories" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {["plumbing", "electrical", "hvac", "handyman", "cleaning", "mechanics", "roofing", "pest-control", "remodeling", "landscaping", "moving", "garage-door", "appliance-repair"].map((cat) => {
              const count = contractors.filter(c => c.services?.includes(cat)).length;
              const jobCount = jobs.filter(j => j.service_category === cat).length;
              return (
                <div key={cat} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900 capitalize">{cat.replace("-", " ")}</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{count}</div>
                      <div className="text-xs text-gray-500">Contractors</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-yellow-600">{jobCount}</div>
                      <div className="text-xs text-gray-500">Jobs</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
              <p className="text-gray-600 mb-6">
                View comprehensive revenue charts, user growth trends, job analytics, and contractor performance metrics.
              </p>
              <Button 
                onClick={() => navigate("/admin/analytics")} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="open-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Open Analytics Dashboard
              </Button>
            </div>

            {/* Quick Stats Preview */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900">Revenue Overview</h3>
                <Button variant="outline" onClick={() => exportData("contractors")} className="h-9 rounded-lg text-sm">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">${stats?.overview?.total_revenue?.toFixed(0) || 0}</div>
                  <div className="text-xs text-green-600">Total Revenue</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">${stats?.overview?.week_revenue?.toFixed(0) || 0}</div>
                  <div className="text-xs text-blue-600">This Week</div>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">${stats?.subscriptions?.mrr?.toFixed(0) || 0}</div>
                  <div className="text-xs text-yellow-600">MRR</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-gray-600">{stats?.overview?.total_transactions || 0}</div>
                  <div className="text-xs text-gray-600">Transactions</div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Top Categories</h3>
                <div className="space-y-3">
                  {Object.entries(stats?.category_breakdown || {}).map(([cat, count]) => (
                    <div key={cat} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 capitalize">{cat}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${Math.min((count / Math.max(...Object.values(stats?.category_breakdown || {1:1}))) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-6">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Top Contractors</h3>
                <div className="space-y-3">
                  {contractors.filter(c => c.rating > 0).sort((a,b) => b.rating - a.rating).slice(0, 5).map((c, idx) => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? "bg-yellow-400 text-white" : "bg-blue-100 text-blue-600"
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-900">{c.business_name || c.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-medium">{c.rating?.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div className="space-y-6">
            {/* Platform Fee Card */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Platform Fee (Commission)
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-2">Fee charged on each booking transaction</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={platformFee}
                      onChange={(e) => setPlatformFee(e.target.value)}
                      className="w-24 h-10"
                      data-testid="platform-fee-input"
                    />
                    <span className="text-gray-600">%</span>
                    <Button
                      onClick={() => updatePlatformFee(platformFee)}
                      className="bg-green-600 hover:bg-green-700 rounded-lg h-10"
                      data-testid="save-platform-fee-btn"
                    >
                      Save
                    </Button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">${bookingStats.total_platform_revenue?.toFixed(2) || "0.00"}</p>
                  <p className="text-xs text-gray-500">Total Platform Revenue</p>
                </div>
              </div>
            </div>

            {/* Pricing Plans */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Subscription & Lead Pricing</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.values(pricing).map((plan) => (
                  <div key={plan.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors" data-testid={`pricing-plan-${plan.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{plan.name}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingPlan(plan); setShowPricingModal(true); }}
                        data-testid={`edit-plan-${plan.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mb-2">${plan.price}</p>
                    <p className="text-sm text-gray-500 mb-3">{plan.description}</p>
                    <ul className="space-y-1">
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-6">
            {/* Booking Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm text-gray-500">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{bookingStats.total_bookings || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm text-gray-500">Platform Revenue</p>
                <p className="text-2xl font-bold text-green-600">${bookingStats.total_platform_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm text-gray-500">Pending Payouts</p>
                <p className="text-2xl font-bold text-orange-600">${bookingStats.pending_payouts?.toFixed(2) || "0.00"}</p>
              </div>
            </div>

            {/* Bookings List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Recent Bookings</h3>
              </div>
              {bookings.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No bookings yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="p-4 hover:bg-gray-50" data-testid={`booking-${booking.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{booking.customer_name}</p>
                          <p className="text-sm text-gray-500">Contractor: {booking.worker_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">${booking.agreed_price}</p>
                          <p className="text-xs text-gray-500">Fee: ${booking.platform_fee}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            booking.status === "completed" ? "bg-green-100 text-green-700" :
                            booking.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                            booking.status === "accepted" ? "bg-blue-100 text-blue-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>
                            {booking.status}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            booking.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                          }`}>
                            {booking.payment_status}
                          </span>
                        </div>
                        {booking.status === "completed" && booking.payment_status === "paid" && booking.payment_release_status !== "released" && (
                          <Button
                            size="sm"
                            onClick={() => releasePayment(booking.id)}
                            className="bg-green-600 hover:bg-green-700 rounded-lg text-xs"
                            data-testid={`release-payment-${booking.id}`}
                          >
                            Release ${booking.contractor_payout}
                          </Button>
                        )}
                        {booking.payment_release_status === "released" && (
                          <span className="text-xs text-green-600 font-medium">Payment Released</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Campaigns Tab */}
        {activeTab === "email-campaigns" && (
          <div className="space-y-6">
            {/* Campaign Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Total Contractors</p>
                <p className="text-2xl font-bold text-gray-900">{emailCampaigns.total_workers}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Complete Profiles</p>
                <p className="text-2xl font-bold text-green-600">{emailCampaigns.complete_profiles}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Incomplete Profiles</p>
                <p className="text-2xl font-bold text-orange-600">{emailCampaigns.incomplete_profiles}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Completion Rate</p>
                <p className="text-2xl font-bold text-purple-600">
                  {emailCampaigns.total_workers > 0 
                    ? Math.round((emailCampaigns.complete_profiles / emailCampaigns.total_workers) * 100) 
                    : 0}%
                </p>
              </div>
            </div>

            {/* Campaign Actions */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Campaign Management</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={fetchEmailCampaigns}
                    variant="outline"
                    className="rounded-lg"
                    disabled={campaignLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${campaignLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    onClick={triggerCampaignProcessing}
                    className="bg-blue-600 hover:bg-blue-700 rounded-lg"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Process Now
                  </Button>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                Automated emails run every hour. The system sends welcome emails on signup, 
                profile reminders at Day 2 and Day 5 (if incomplete), and activation emails at Day 10.
              </p>

              {/* Email Type Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {[
                  { type: "welcome", label: "Welcome (Day 0)", color: "blue" },
                  { type: "profile_reminder", label: "Profile Reminder (Day 2)", color: "yellow" },
                  { type: "conversion_boost", label: "Conversion Boost (Day 5)", color: "red" },
                  { type: "lead_activation", label: "Lead Activation (Day 10)", color: "green" }
                ].map(({ type, label, color }) => {
                  const stats = emailCampaigns.stats_by_type?.[type] || { total: 0, successful: 0, failed: 0 };
                  return (
                    <div key={type} className={`p-3 rounded-lg bg-${color}-50 border border-${color}-200`}>
                      <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-gray-900">{stats.total}</span>
                        <div className="text-xs text-gray-500">
                          <span className="text-green-600">{stats.successful} ✓</span>
                          {stats.failed > 0 && <span className="ml-1 text-red-600">{stats.failed} ✗</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Email Flow Diagram */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Email Flow</h3>
              <div className="flex items-center justify-between overflow-x-auto pb-2">
                {[
                  { day: "Day 0", title: "Welcome", desc: "Sent immediately", icon: "🎉" },
                  { day: "Day 2", title: "Profile Reminder", desc: "If profile incomplete", icon: "📸" },
                  { day: "Day 5", title: "Conversion Boost", desc: "If still incomplete", icon: "⚠️" },
                  { day: "Day 10", title: "Lead Activation", desc: "Tips for success", icon: "🎯" }
                ].map((step, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="flex-shrink-0 bg-gray-50 rounded-lg p-4 text-center min-w-[140px]">
                      <span className="text-2xl mb-2 block">{step.icon}</span>
                      <p className="text-xs text-blue-600 font-medium">{step.day}</p>
                      <p className="font-medium text-gray-900 text-sm">{step.title}</p>
                      <p className="text-xs text-gray-500">{step.desc}</p>
                    </div>
                    {idx < 3 && (
                      <div className="flex-shrink-0 px-2">
                        <ArrowUpRight className="w-5 h-5 text-gray-300 rotate-45" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Emails */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Recent Emails Sent</h3>
              </div>
              {emailCampaigns.recent_emails?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {emailCampaigns.recent_emails?.map((email, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{email.worker_email}</p>
                          <p className="text-sm text-gray-500">{email.subject}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            email.campaign_type === "welcome" ? "bg-blue-100 text-blue-700" :
                            email.campaign_type === "profile_reminder" ? "bg-yellow-100 text-yellow-700" :
                            email.campaign_type === "conversion_boost" ? "bg-red-100 text-red-700" :
                            "bg-green-100 text-green-700"
                          }`}>
                            {email.campaign_type}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(email.sent_at).toLocaleDateString()}
                          </p>
                          {!email.success && (
                            <p className="text-xs text-red-500 mt-1">Failed</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Feature Toggles</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Referral Program</p>
                    <p className="text-xs text-gray-500">Contractors earn leads through referrals</p>
                  </div>
                  <Switch 
                    checked={featureToggles.referral_program !== false} 
                    onCheckedChange={(checked) => updateFeatureToggle("referral_program", checked)}
                    data-testid="toggle-referral"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Public Job Board</p>
                    <p className="text-xs text-gray-500">Homeowners can post public jobs</p>
                  </div>
                  <Switch 
                    checked={featureToggles.public_job_board !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("public_job_board", checked)}
                    data-testid="toggle-job-board"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">AI Job Matching</p>
                    <p className="text-xs text-gray-500">Auto-match leads to contractors</p>
                  </div>
                  <Switch 
                    checked={featureToggles.ai_job_matching !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("ai_job_matching", checked)}
                    data-testid="toggle-ai-matching"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Crypto Payments</p>
                    <p className="text-xs text-gray-500">Accept cryptocurrency</p>
                  </div>
                  <Switch 
                    checked={featureToggles.crypto_payments !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("crypto_payments", checked)}
                    data-testid="toggle-crypto"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Instant Booking</p>
                    <p className="text-xs text-gray-500">Enable direct booking with platform fees</p>
                  </div>
                  <Switch 
                    checked={featureToggles.instant_booking !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("instant_booking", checked)}
                    data-testid="toggle-booking"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Push Notifications</p>
                    <p className="text-xs text-gray-500">Browser push notifications for users</p>
                  </div>
                  <Switch 
                    checked={featureToggles.push_notifications !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("push_notifications", checked)}
                    data-testid="toggle-push"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Availability Calendar</p>
                    <p className="text-xs text-gray-500">Contractors can set availability</p>
                  </div>
                  <Switch 
                    checked={featureToggles.availability_calendar !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("availability_calendar", checked)}
                    data-testid="toggle-calendar"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Quote Builder</p>
                    <p className="text-xs text-gray-500">Contractors can create formal quotes</p>
                  </div>
                  <Switch 
                    checked={featureToggles.quote_builder !== false}
                    onCheckedChange={(checked) => updateFeatureToggle("quote_builder", checked)}
                    data-testid="toggle-quotes"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Integrations</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Search className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Google Search Console</p>
                      <p className="text-xs text-gray-500">SEO monitoring</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Connected</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Stripe</p>
                      <p className="text-xs text-gray-500">Payment processing</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Connected</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Notification Modal */}
      <Dialog open={showNotificationModal} onOpenChange={setShowNotificationModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Send Geo-Targeted Alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Target City (optional)</Label>
              <Input
                value={notification.targetCity}
                onChange={(e) => setNotification({...notification, targetCity: e.target.value})}
                placeholder="e.g., Phoenix"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label className="text-sm">Title</Label>
              <Input
                value={notification.title}
                onChange={(e) => setNotification({...notification, title: e.target.value})}
                placeholder="New jobs available!"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label className="text-sm">Message</Label>
              <Textarea
                value={notification.message}
                onChange={(e) => setNotification({...notification, message: e.target.value})}
                placeholder="We have new opportunities..."
                className="mt-1 rounded-lg"
                rows={3}
              />
            </div>
            <Button onClick={sendGeoNotification} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Contractor Confirmation */}
      <AlertDialog open={deleteContractorDialog.open} onOpenChange={(open) => !deleting && setDeleteContractorDialog({ ...deleteContractorDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Contractor
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteContractorDialog.name}</strong>? This action cannot be undone. All associated reviews, applications, and leads will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteContractor(deleteContractorDialog.id)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Job Confirmation */}
      <AlertDialog open={deleteJobDialog.open} onOpenChange={(open) => !deleting && setDeleteJobDialog({ ...deleteJobDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Job
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the job <strong>"{deleteJobDialog.title}"</strong>? This action cannot be undone. All applications for this job will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteJob(deleteJobDialog.id)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pricing Edit Modal */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Edit Pricing Plan</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Plan Name</Label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                  className="mt-1 rounded-lg"
                  data-testid="edit-plan-name"
                />
              </div>
              <div>
                <Label className="text-sm">Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingPlan.price}
                  onChange={(e) => setEditingPlan({...editingPlan, price: parseFloat(e.target.value)})}
                  className="mt-1 rounded-lg"
                  data-testid="edit-plan-price"
                />
              </div>
              {editingPlan.leads_included !== undefined && (
                <div>
                  <Label className="text-sm">Leads Included (-1 for unlimited)</Label>
                  <Input
                    type="number"
                    value={editingPlan.leads_included}
                    onChange={(e) => setEditingPlan({...editingPlan, leads_included: parseInt(e.target.value)})}
                    className="mt-1 rounded-lg"
                    data-testid="edit-plan-leads"
                  />
                </div>
              )}
              {editingPlan.duration_days !== undefined && (
                <div>
                  <Label className="text-sm">Duration (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editingPlan.duration_days}
                    onChange={(e) => setEditingPlan({...editingPlan, duration_days: parseInt(e.target.value)})}
                    className="mt-1 rounded-lg"
                    data-testid="edit-plan-duration"
                  />
                </div>
              )}
              <div>
                <Label className="text-sm">Description</Label>
                <Input
                  value={editingPlan.description}
                  onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                  className="mt-1 rounded-lg"
                  data-testid="edit-plan-description"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPricingModal(false)}>Cancel</Button>
                <Button 
                  onClick={() => updatePricing(editingPlan.id, editingPlan)} 
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-pricing-btn"
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
